import AddCommentIcon from "@mui/icons-material/AddComment";
import ResolveIcon from "@mui/icons-material/AssignmentTurnedIn";
import ForumIcon from "@mui/icons-material/Forum";
import MessageIcon from "@mui/icons-material/Message";
import PageviewIcon from "@mui/icons-material/Pageview";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, Theme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import EmptyInboxPlaceholder from "_shared/_views/assets/empty-inbox-all-caught-up.svg";
import Loader from "_shared/_views/loading/loader";
import { MRTNoMemo } from "_shared/_views/tables/table";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_TableInstance as TableInstance,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
} from "material-react-table";
import ForumQuery, {
  ForumFilterParameters,
  ForumOrderField,
  ForumOrderParameters,
} from "messaging/forums/_api/queries/forum-query";
import {
  getErrorLoadingForumsByQuery,
  getForumQueryByInterface,
  getForumsByQuery,
  getIsLoadingForumsByQuery,
  populateForumsByQuery,
  updateForumInterfaceQuery,
} from "messaging/forums/_stores/forums-redux-slice";
import Forum from "messaging/forums/forum";
import ForumName from "notifications/values/forum-name/forum-name";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";

const NoRowsContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
}));
const RowActionsContainer = styled(Box)(() => ({
  display: "flex",
  flexWrap: "nowrap",
  width: "inherit",
}));
const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const ToolbarActionButtons = styled("div")(() => ({
  textAlign: "right",
  whiteSpace: "nowrap",
}));
const CategoryItem = styled(ListItem)(() => ({
  margin: 0,
  padding: 0,
}));
const AlertActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  height: "100%",
  paddingRight: theme.spacing(2),
  whiteSpace: "nowrap",
}));
const AllCaughtUp = styled("img")(({ theme }) => ({
  maxHeight: "100px",
  padding: theme.spacing(2),
}));
const NewForumButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  paddingRight: theme.spacing(1),
}));
const ComposeMessageButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  paddingRight: theme.spacing(1),
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

type ConversationTableProps = {
  renderTopToolbarCustomActions: (props: { table: TableInstance<Forum> }) => React.ReactNode;
};

  const columnHelper = createMRTColumnHelper<Forum>();

export default function ConversationTable(props: Readonly<ConversationTableProps>) {
  const { renderTopToolbarCustomActions } = props;
  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});

  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const navigate = useNavigate();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const isSmlDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isMedDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const interfaceName = "forumsIndex";

  const dispatch = useAttorneyHubDispatch();

  const forumQuery = useSelector(getForumQueryByInterface(interfaceName));
  const forums = useSelector(getForumsByQuery(forumQuery));
  const queryIsLoading = useSelector(getIsLoadingForumsByQuery(forumQuery));
  const errorLoadingQuery = useSelector(getErrorLoadingForumsByQuery(forumQuery));

  const showAlertBanner = Boolean(errorLoadingQuery);
  const showProgressBars = queryIsLoading;
  const showSkeletons = queryIsLoading && forums === undefined && !errorLoadingQuery;

  const defaultQuery = React.useMemo(
    () =>
      new ForumQuery({
        filterParameters: new ForumFilterParameters({}),
        orderParameters: new ForumOrderParameters({
          orderBy: ForumOrderField.CreatedDate,
          orderDir: OrderDirection.Descending,
        }),
        paginationParameters: PaginationParameters.create({
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        }),
      }),
    [pagination.pageIndex, pagination.pageSize]
  );

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (!forumQuery) {
      dispatch(updateForumInterfaceQuery({ interfaceName, query: defaultQuery }));
      return;
    }

    if (forumQuery && forums === undefined && !queryIsLoading) {
      dispatch(
        populateForumsByQuery({
          session: session,
          query: forumQuery,
        }),
      );
    } 
  }, [forums, forumQuery, globalFilter, queryIsLoading, dispatch, defaultQuery, session]);

  useEffect(() => {
    const forumNameFilter = ForumName.fromJSON(columnFilters.find((f) => f.id === "conversationName")?.value);
    if (!forumNameFilter) return;
    const filterParameters = new ForumFilterParameters({
      name: forumNameFilter,
    });
    let orderBy = ForumOrderField.CreatedDate;
    let orderDir = OrderDirection.Descending;
    if (sorting.length > 0) {
      const sort = sorting[0];
      orderBy = sort.id as ForumOrderField;
      orderDir = sort.desc ? OrderDirection.Descending : OrderDirection.Ascending;
    }
    const orderParameters = new ForumOrderParameters({ orderBy, orderDir });
    const newQuery = new ForumQuery({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.create({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      }),
      query: globalFilter,
    });
    if (newQuery.isEqualTo(forumQuery)) return;
    dispatch(
      updateForumInterfaceQuery({
        interfaceName: "forumsIndex",
        query: newQuery,
      }),
    );
  }, [columnFilters, dispatch, forumQuery, globalFilter, pagination, sorting]);

  async function navigateToTarget(forum: Forum) {
    if (forum?.id) {
      navigate(`/communications/forums/${forum.id}`);
    }
  }

  const renderConversationNameColumn = useCallback((forum: Forum): React.ReactNode => {
    return (
      <CategoryItem dense>
        <ListItemIcon>
          <MessageIcon />
        </ListItemIcon>
        <ListItemText
          primary={forum?.name?.toString() ?? ""}
          slotProps={{
            primary: { 
              sx: {
                fontWeight: "500"
              }
            },
          }}
        />
      </CategoryItem>
    );
  }, []);

  const renderCreatedDateColumn = useCallback((forum: Forum): React.ReactNode => {
    if (!forum.createdDate) return null;
    return <time>{forum.createdDate.format("MM/DD/YY hh:mm A")}</time>;
  }, []);

  const columns = useMemo(() => [
    columnHelper.accessor(renderConversationNameColumn, {
      header: "Conversation Name",
      id: "name",
      enableSorting: true,
      enableColumnFilter: true,
    }),
    columnHelper.accessor(renderCreatedDateColumn, {
      header: "Created",
      id: "createdDate",
    })
  ], [renderConversationNameColumn, renderCreatedDateColumn]);

  function renderForumRowActions(forum: Forum): React.ReactNode {
    return (
      <RowActionsContainer>
        <Tooltip title="View Conversation">
          <IconButton
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/communications/forums/${forum.id}`);
            }}
            size="small"
          >
            <PageviewIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Resolve">
          <IconButton
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
            }}
            size="small"
          >
            <ResolveIcon />
          </IconButton>
        </Tooltip>
      </RowActionsContainer>
    );
  }

  const tableOptions: MRT_TableOptions<Forum> = {
    columns,
    data: forums ?? [],
    rowCount: forums?.length,
    enableRowSelection: false,
    enableRowActions: true,
    enableTableHead: true,
    renderRowActions: ({ row }) => renderForumRowActions(row.original),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        void navigateToTarget(row.original);
      },
      sx: {
        cursor: "pointer",
        backgroundColor: "inherit",
      },
    }),
    muiTableProps: () => ({
      sx: {
        height: forums?.length === 0 ? "100%" : "initial",
      },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMedDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <ToolbarActionButtons>
              <Button
                color="primary"
                startIcon={<AddCommentIcon />}
                onClick={() => navigate("/communications/forums/compose")}
              >
                Compose Message
              </Button>
              <Button
                color="primary"
                startIcon={<ForumIcon />}
                onClick={() => navigate("/communications/forums/create")}
              >
                New Conversation
              </Button>
            </ToolbarActionButtons>
          </ToolbarButtonsContainer>
        )}
        {isMedDisplaySize && !isSmlDisplaySize && (
          <>
            <ComposeMessageButton color="primary" onClick={() => navigate("/communications/forums/compose")}>
              <AddCommentIcon fontSize="large" />
            </ComposeMessageButton>
            <NewForumButton color="primary" onClick={() => navigate("/communications/forums/create")}>
              <ForumIcon fontSize="large" />
            </NewForumButton>
          </>
        )}
      </>
    ),
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    renderEmptyRowsFallback: () => {
      if (queryIsLoading) {
        return;
      }
      return (
        <NoRowsContainer>
          <AllCaughtUp alt="All Caught Up" src={EmptyInboxPlaceholder} />
        </NoRowsContainer>
      );
    },
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      columnOrder: ["name", "createdDate", "actions"],
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnPinning: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: { sx: { flex: 1 } },
    muiTablePaperProps: { sx: { height: "100%", display: "flex", flexDirection: "column" } },
    muiToolbarAlertBannerProps: showAlertBanner
      ? {
          color: "error",
          title: errorLoadingQuery || "",
        }
      : {
          action: <AlertActions />,
        },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      globalFilter,
      isLoading: queryIsLoading,
      pagination,
      showAlertBanner,
      showProgressBars,
      sorting,
      rowSelection,
      showSkeletons,
    },
  }

  if (!sessionPermissions) return <LoaderContainer><Loader fontSize="20px" /></LoaderContainer>;
  if (!sessionPermissions.includesAccessToCommunications) return <NotAuthorized />;

  return <MRTNoMemo<Forum> options={tableOptions} />;
}
